<template>
  <el-dialog
    :title="`提现余额 - ${currentUser ? currentUser.user_name || currentUser.mobile : ''}`"
    :visible.sync="dialogVisible"
    width="500px"
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <el-form
      ref="withdrawalForm"
      :model="form"
      :rules="rules"
      label-width="100px"
      class="withdrawal-form"
    >
      <el-form-item label="当前余额">
        <el-input v-model="currentBalance" disabled class="disabled-input">
          <template slot="append">元</template>
        </el-input>
      </el-form-item>

      <el-form-item label="提现金额" prop="balance">
        <el-input
          v-model="form.balance"
          type="number"
          placeholder="请输入提现金额"
          :min="0.01"
          :step="0.01"
          :precision="2"
          @input="handleBalanceInput"
        >
          <template slot="append">元</template>
        </el-input>
        <div v-if="isInsufficient" class="hint-text insufficient">
          <i class="el-icon-warning"></i> 余额不足
        </div>
        <div v-else-if="form.balance > 0" class="hint-text success">
          <i class="el-icon-success"></i> 提现后余额: {{ afterBalance }}元
        </div>
      </el-form-item>

      <el-form-item label="提现原因" prop="reason">
        <el-input
          v-model="form.reason"
          type="textarea"
          :rows="3"
          placeholder="请输入提现原因（1-200字符）"
          maxlength="200"
          show-word-limit
        />
      </el-form-item>
    </el-form>

    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button
        type="danger"
        :loading="submitting"
        :disabled="isInsufficient"
        @click="handleSubmit"
      >确认提现</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { withdrawalBalances } from '@/api/user'

export default {
  name: 'WithdrawalBalanceDialog',
  data() {
    return {
      dialogVisible: false,
      currentUser: null,
      currentBalance: '0.00',
      form: {
        user_id: 0,
        balance: '',
        reason: ''
      },
      rules: {
        balance: [
          { required: true, message: '请输入提现金额', trigger: 'blur' },
          {
            validator: (rule, value, callback) => {
              if (!value || parseFloat(value) <= 0) {
                callback(new Error('提现金额必须大于0'))
              } else if (parseFloat(value) > parseFloat(this.currentBalance)) {
                callback(new Error('提现金额不能超过当前余额'))
              } else if (parseFloat(value) > 999999) {
                callback(new Error('提现金额不能超过999999元'))
              } else {
                callback()
              }
            },
            trigger: 'blur'
          }
        ],
        reason: [
          { required: true, message: '请输入提现原因', trigger: 'blur' },
          { min: 1, max: 200, message: '提现原因长度为1-200字符', trigger: 'blur' }
        ]
      },
      submitting: false
    }
  },
  computed: {
    // 是否余额不足
    isInsufficient() {
      if (!this.form.balance) return false
      const balanceValue = parseFloat(this.form.balance)
      const currentBalanceValue = parseFloat(this.currentBalance)
      return balanceValue > currentBalanceValue
    },
    // 提现后余额
    afterBalance() {
      if (!this.form.balance) return this.currentBalance
      const balanceValue = parseFloat(this.form.balance)
      const currentBalanceValue = parseFloat(this.currentBalance)
      return (currentBalanceValue - balanceValue).toFixed(2)
    }
  },
  methods: {
    // 打开对话框
    open(user) {
      this.currentUser = user
      this.form.user_id = user.id
      this.form.balance = ''
      this.form.reason = ''
      this.currentBalance = Number(user.balance || 0).toFixed(2)
      this.dialogVisible = true

      // 重置表单验证
      this.$nextTick(() => {
        if (this.$refs.withdrawalForm) {
          this.$refs.withdrawalForm.clearValidate()
        }
      })
    },

    // 关闭对话框
    handleClose() {
      this.dialogVisible = false
    },

    // 金额输入处理
    handleBalanceInput(value) {
      // 限制为正数，最多2位小数
      const regex = /^\d+(\.\d{0,2})?$/
      if (!regex.test(value)) {
        this.$nextTick(() => {
          this.form.balance = value.replace(/[^\d.]/g, '').replace(/^(\d+\.\d{2}).+$/, '$1')
        })
      }
    },

    // 提交提现
    async handleSubmit() {
      // 表单验证
      this.$refs.withdrawalForm.validate(async (valid) => {
        if (!valid) return

        // 再次校验余额是否充足
        if (this.isInsufficient) {
          this.$message.error('余额不足，请检查提现金额')
          return
        }

        // 确认提示
        const balanceValue = parseFloat(this.form.balance).toFixed(2)
        const afterBalanceValue = this.afterBalance
        this.$confirm(
          `确认给用户「${this.currentUser.user_name || this.currentUser.mobile}」提现${balanceValue}元？提现后余额为${afterBalanceValue}元`,
          '提现确认',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        ).then(async () => {
          this.submitting = true
          try {
            const res = await withdrawalBalances({
              user_id: this.form.user_id,
              balance: parseFloat(this.form.balance),
              reason: this.form.reason
            })

            if (res.code === 200) {
              this.$message.success('提现成功')
              this.dialogVisible = false
              this.$emit('refresh')
            } else {
              this.$message.error(res.msg || res.message || '提现失败')
            }
          } catch (error) {
            console.error('提现失败', error)
            this.$message.error(error.message || '网络错误，请重试')
          } finally {
            this.submitting = false
          }
        }).catch(() => {})
      })
    }
  }
}
</script>

<style scoped>
.withdrawal-form {
  padding: 0 20px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* 提现金额输入框样式 */
.withdrawal-form :deep(.el-input__inner) {
  text-align: right;
  padding-right: 40px;
}

.withdrawal-form :deep(.el-input-group__append) {
  padding: 0 12px;
  background-color: #f5f7fa;
  border-left: 1px solid #dcdfe6;
  color: #909399;
}

/* 当前余额输入框样式 */
.withdrawal-form :deep(.el-input.is-disabled .el-input__inner) {
  background-color: #f5f7fa;
  color: #606266;
}

/* 提示文字样式 */
.hint-text {
  margin-top: 8px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.hint-text.insufficient {
  color: #f56c6c;
}

.hint-text.success {
  color: #67c23a;
}
</style>
